class VaillantProductPurchase {
  constructor(productInfo) {
    this.productInfo = productInfo;
    this.refreshElements();

    this.requiresSizeSelection =
      this.form?.dataset.requireSizeSelection === 'true' ||
      this.getVariantSelects()?.dataset.requireSizeSelection === 'true';

    if (!this.form || !this.submitButton || !this.variantInput || !this.requiresSizeSelection) return;

    this.addToBagLabel = this.submitButton.dataset.addToBagLabel || 'Add to bag';
    this.selectSizeLabel = this.submitButton.dataset.selectSizeLabel || 'Select a size';
    this.submitLabel = this.submitButton.querySelector('span');

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmitClick = this.handleSubmitClick.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleOptionValueChange = this.handleOptionValueChange.bind(this);

    this.productInfo.addEventListener('change', this.handleChange);
    this.submitButton.addEventListener('click', this.handleSubmitClick);
    this.form.querySelector('form')?.addEventListener('submit', this.handleFormSubmit);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      this.optionChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleOptionValueChange
      );
    }

    this.updatePurchaseState();
  }

  destroy() {
    this.productInfo.removeEventListener('change', this.handleChange);
    this.submitButton?.removeEventListener('click', this.handleSubmitClick);
    this.form?.querySelector('form')?.removeEventListener('submit', this.handleFormSubmit);
    this.variantChangeUnsubscriber?.();
    this.optionChangeUnsubscriber?.();
    window.clearTimeout(this.optionChangeTimeout);
  }

  refreshElements() {
    this.form = this.productInfo.querySelector('product-form.vaillant-product__product-form');
    this.submitButton = this.form?.querySelector('.vaillant-product__add-to-bag');
    this.variantInput = this.form?.querySelector('[name="id"]');
  }

  getVariantSelects() {
    return this.productInfo.querySelector('variant-selects');
  }

  isSoldOut() {
    return this.productInfo.classList.contains('vaillant-product-section--sold-out');
  }

  isSizeSelected() {
    const sizeFieldset = this.getVariantSelects()?.querySelector(
      '.vaillant-product__option--size .vaillant-product__size-options'
    );
    if (!sizeFieldset) return true;
    return Boolean(sizeFieldset.querySelector('input:checked'));
  }

  getSelectedVariantData() {
    const script = this.getVariantSelects()?.querySelector('[data-selected-variant]');
    if (!script?.textContent) return null;

    try {
      const data = JSON.parse(script.textContent);
      return data?.id ? data : null;
    } catch {
      return null;
    }
  }

  syncVariantInput() {
    if (!this.variantInput) return false;

    const variant = this.getSelectedVariantData();
    if (variant?.id) {
      this.variantInput.value = variant.id;
      this.variantInput.disabled = false;
      return true;
    }

    return Boolean(this.variantInput.value);
  }

  getSizeError() {
    return this.productInfo.querySelector('.vaillant-product__size-error');
  }

  hideSizeError() {
    const sizeError = this.getSizeError();
    if (sizeError) sizeError.hidden = true;
  }

  showSizeError() {
    const sizeError = this.getSizeError();
    if (sizeError) sizeError.hidden = false;
  }

  updatePurchaseState() {
    this.refreshElements();

    if (!this.form || !this.submitButton || !this.variantInput || !this.requiresSizeSelection) return;
    if (this.isSoldOut()) return;

    if (!this.submitLabel) {
      this.submitLabel = this.submitButton.querySelector('span');
    }

    const sizeSelected = this.isSizeSelected();

    if (!sizeSelected) {
      this.variantInput.value = '';
      this.variantInput.disabled = true;
      this.submitButton.type = 'button';
      this.submitButton.dataset.selectSizeMode = 'true';
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-disabled');
      if (this.submitLabel) this.submitLabel.textContent = this.selectSizeLabel;
      return;
    }

    this.hideSizeError();

    if (!this.syncVariantInput()) return;

    const variant = this.getSelectedVariantData();
    const variantUnavailable = variant?.available === false;

    this.submitButton.type = 'submit';
    this.submitButton.dataset.selectSizeMode = 'false';
    this.variantInput.disabled = variantUnavailable;

    if (variantUnavailable) {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
      if (this.submitLabel) this.submitLabel.textContent = window.variantStrings?.soldOut || 'Sold out';
      return;
    }

    this.submitButton.disabled = false;
    this.submitButton.removeAttribute('disabled');
    this.submitButton.removeAttribute('aria-disabled');
    if (this.submitLabel) this.submitLabel.textContent = this.addToBagLabel;
  }

  handleChange(event) {
    if (!event.target.closest('.vaillant-product__size-options')) return;
    if (this.isSizeSelected()) this.hideSizeError();
    this.updatePurchaseState();
  }

  handleSubmitClick(event) {
    if (this.submitButton?.dataset.selectSizeMode !== 'true') return;

    event.preventDefault();
    this.showSizeError();

    this.productInfo.querySelector('.vaillant-product__option--size')?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'nearest',
    });
  }

  handleFormSubmit(event) {
    if (this.isSizeSelected() && this.variantInput?.value) return;

    event.preventDefault();
    this.showSizeError();
  }

  handleVariantChange({ data: { sectionId, variant } }) {
    if (sectionId !== this.productInfo.dataset.section) return;

    window.requestAnimationFrame(() => {
      if (variant?.id && this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.disabled = false;
      }
      this.updatePurchaseState();
    });
  }

  handleOptionValueChange({ data: { event } }) {
    if (!this.productInfo.contains(event.target)) return;

    window.clearTimeout(this.optionChangeTimeout);
    this.optionChangeTimeout = window.setTimeout(() => {
      this.updatePurchaseState();
    }, 0);
  }

  static init(productInfo) {
    if (productInfo.__vaillantPurchase) {
      productInfo.__vaillantPurchase.destroy();
    }

    productInfo.__vaillantPurchase = new VaillantProductPurchase(productInfo);
    return productInfo.__vaillantPurchase;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('product-info.vaillant-product-section').forEach((productInfo) => {
    VaillantProductPurchase.init(productInfo);
  });
});

document.addEventListener('product-info:loaded', (event) => {
  if (event.target.matches('product-info.vaillant-product-section')) {
    VaillantProductPurchase.init(event.target);
  }
});
